import React, { useState, useEffect } from 'react';
import { getRecommendedProducts } from '../../services/productService';
import ProductCard from '../sections/ProductCard';
import '../../styles/productDetail.css';
import '../../styles/sectionPage.css';

/**
 * RelatedProducts component redesigned for Phase 2 backend-driven recommended products.
 * Fetches pre-processed recommendation list (category matches, falling back to section matches)
 * and renders a grid on desktop/tablet and a horizontal swipe snap track on mobile.
 *
 * @param {Object} props
 * @param {string} props.currentProductId - The ID of the current active product
 * @param {string} props.sectionColor - Accent color for styling the cards
 */
const RelatedProducts = ({ currentProductId, sectionColor }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch recommendations directly on mount or when currentProductId changes
  useEffect(() => {
    let isMounted = true;

    const fetchRecommendations = async () => {
      try {
        setLoading(true);
        const data = await getRecommendedProducts(currentProductId);
        if (isMounted) {
          setProducts(data);
        }
      } catch (err) {
        console.error('Error fetching recommended products:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (currentProductId) {
      fetchRecommendations();
    }

    return () => {
      isMounted = false;
    };
  }, [currentProductId]);

  // Empty State: Hide the entire section if no recommendations exist
  if (!loading && products.length === 0) {
    return null;
  }

  // Render Skeleton Cards (Desktop: 4 grid items, Mobile: responsive matching)
  const renderSkeletons = () => {
    const skeletonsCount = window.innerWidth <= 768 ? 3 : 4;
    const skeletonPlaceholders = Array(skeletonsCount).fill(0);

    return skeletonPlaceholders.map((_, idx) => (
      <div className="product-skeleton-card" key={`recommend-skeleton-${idx}`}>
        {/* Shimmer Image Frame */}
        <div className="product-skeleton-image skeleton-pulse" />
        
        {/* Shimmer Details Frame */}
        <div className="product-skeleton-info">
          <div className="product-skeleton-title skeleton-pulse" />
          <div className="product-skeleton-desc skeleton-pulse" />
          <div className="product-skeleton-desc skeleton-pulse" style={{ width: '70%' }} />
          <div className="product-skeleton-meta">
            <div className="product-skeleton-price skeleton-pulse" />
            <div className="product-skeleton-moq skeleton-pulse" />
          </div>
          <div className="product-skeleton-button skeleton-pulse" />
        </div>
      </div>
    ));
  };

  return (
    <div className="related-products-section">
      <h3 className="related-products-title">You May Also Like</h3>
      <p className="related-products-subtitle">Similar products you may be interested in</p>
      
      <div className="related-products-grid">
        {loading ? (
          renderSkeletons()
        ) : (
          products.map((product) => (
            <ProductCard
              key={product._id}
              product={product}
              sectionColor={sectionColor}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default RelatedProducts;
